import { useQuery } from '@tanstack/react-query';
import { DEVISE_REFERENCE } from '@mirsad/shared';
import { api } from '@/lib/api';

/**
 * Devise d'affichage des prix.
 *
 * Les prix arrivent de l'API en euros, la devise de référence des relevés (ou,
 * pour les variantes, dans la devise de la boutique). L'interface les convertit
 * au taux le plus récent saisi par l'administrateur. Sans taux connu, un
 * montant reste dans sa devise d'origine : mieux vaut un prix en euros qu'un
 * prix converti au hasard.
 *
 * L'état vit au niveau du module, comme la langue d'une interface : les
 * fonctions de format sont appelées partout, hors des composants React.
 * `AppShell` le renseigne à chaque rendu, avant ses enfants.
 */

export interface Devises {
  reference: string;
  /** Devise effectivement affichée : la demandée si elle a un taux, l'euro sinon. */
  affichage: string;
  demandee: string;
  taux: { devise: string; taux: number; depuis: string }[];
  historique: { devise: string; taux: number; depuis: string; saisiPar: string | null }[];
}

let affichage = DEVISE_REFERENCE;
/** Unités de chaque devise pour 1 EUR. */
let taux = new Map<string, number>([[DEVISE_REFERENCE, 1]]);

export function definirDevises(d: Devises | undefined): void {
  if (!d) return;
  taux = new Map([[DEVISE_REFERENCE, 1], ...d.taux.map((t) => [t.devise, t.taux] as const)]);
  affichage = taux.has(d.affichage) ? d.affichage : DEVISE_REFERENCE;
}

export const useDevises = () =>
  useQuery({
    queryKey: ['settings', 'devises'],
    queryFn: async () => (await api.get<Devises>('/settings/devises')).data,
    staleTime: 5 * 60_000,
  });

/** Devise dans laquelle l'interface affiche les prix (code ISO). */
export const deviseAffichee = (): string => affichage;

/**
 * Montant d'une devise vers la devise d'affichage, en passant par l'euro.
 * `null` quand le taux de la devise d'origine n'est pas connu.
 */
export function convertir(montant: number, devise: string = DEVISE_REFERENCE): number | null {
  const depuis = taux.get(devise);
  const vers = taux.get(affichage);
  if (!depuis || !vers) return null;
  return (montant / depuis) * vers;
}

/** Montant de la devise d'affichage vers l'euro : une borne saisie à l'écran part en euros à l'API. */
export function versEuros(montant: number): number {
  const vers = taux.get(affichage) ?? 1;
  return montant / vers;
}

function formatter(devise: string, decimales: number): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: devise,
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  } catch {
    // Un code de devise inconnu du navigateur : le montant reste lisible.
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }
}

/**
 * Prix lisible dans la devise d'affichage : « 7,50 € », « 81,38 MAD »,
 * « 1 240 € ». Les centimes s'affichent sous 100 (un « 8 € » cachait un
 * 7,50 €) ; au-delà, l'unité suffit. `precis` garde toujours les centimes.
 */
export function formaterPrix(
  valeur: number | string | null | undefined,
  { devise, precis = false }: { devise?: string | null; precis?: boolean } = {},
): string {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  const brut = Number(valeur);
  if (!Number.isFinite(brut)) return '—';
  const origine = devise || DEVISE_REFERENCE;
  const converti = convertir(brut, origine);
  const montant = converti ?? brut;
  const cible = converti === null ? origine : affichage;
  const centimes = precis || (Math.abs(montant) < 100 && Math.round(montant * 100) % 100 !== 0);
  const texte = formatter(cible, centimes ? 2 : 0).format(montant);
  return converti === null && !texte.includes(cible) && cible !== DEVISE_REFERENCE
    ? `${texte} ${cible}`
    : texte;
}

/** Symbole court de la devise d'affichage, pour les axes et les champs (« € », « MAD »). */
export function symboleDevise(): string {
  const partie = formatter(affichage, 0)
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return partie?.value ?? affichage;
}
